import { useEffect, useMemo, useState } from "react";
import { History, Sparkles, X } from "lucide-react";
import { FormField, inputClass, selectClass } from "../components/FormField";
import {
  createAdminBudgetRevision,
  fetchAdminBudgetRevisions,
  suggestAdminBudgetRevision,
  type AdminBudgetConsumptionItem,
  type AdminBudgetRevision,
  type AdminBudgetRevisionSuggestion,
} from "../api/adminApi";
import { cn } from "@/shared/lib/cn";

function formatUsd(value: number | null | undefined) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

function naturesForType(type: string): Array<{ value: string; label: string }> {
  switch (type) {
    case "forfaitaire":
      return [{ value: "forfait", label: "Forfait" }];
    case "mixte":
      return [
        { value: "forfait", label: "Forfait" },
        { value: "hors_forfait", label: "Hors forfait" },
      ];
    default:
      return [{ value: "global", label: "Budget global" }];
  }
}

type BudgetRevisionPanelProps = {
  row: AdminBudgetConsumptionItem;
  onClose: () => void;
  onSaved: (item: AdminBudgetConsumptionItem) => void;
  labels: Record<string, string>;
};

export function BudgetRevisionPanel({ row, onClose, onSaved, labels }: BudgetRevisionPanelProps) {
  const natures = useMemo(() => naturesForType(row.type_traitement), [row.type_traitement]);
  const [tab, setTab] = useState<"revise" | "history">("revise");
  const [budgetNature, setBudgetNature] = useState(natures[0]?.value ?? "global");
  const [effectiveFrom, setEffectiveFrom] = useState(row.period_start);
  const [newAmount, setNewAmount] = useState(String(row.budget ?? ""));
  const [reason, setReason] = useState("");
  const [suggestion, setSuggestion] = useState<AdminBudgetRevisionSuggestion | null>(null);
  const [history, setHistory] = useState<AdminBudgetRevision[]>([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSuggest = row.type_traitement === "forfaitaire" || row.type_traitement === "mixte";

  useEffect(() => {
    setBudgetNature(natures[0]?.value ?? "global");
  }, [natures]);

  useEffect(() => {
    if (tab !== "history") return;
    let cancelled = false;
    void (async () => {
      setLoadingHistory(true);
      try {
        const res = await fetchAdminBudgetRevisions(row.convention_id, row.period_key);
        if (!cancelled) setHistory(res.items);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Historique indisponible.");
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, row.convention_id, row.period_key]);

  const runSuggest = async () => {
    if (!canSuggest) return;
    setLoadingSuggest(true);
    setError(null);
    try {
      const res = await suggestAdminBudgetRevision(row.convention_id, {
        period_key: row.period_key,
        effective_from: effectiveFrom,
        budget_nature: budgetNature,
      });
      setSuggestion(res.suggestion);
      setNewAmount(String(res.suggestion.suggested_new_amount ?? row.budget ?? ""));
      if (res.suggestion.detail) {
        setReason((prev) => prev || `Ajustement effectif : ${res.suggestion.detail}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Suggestion impossible.");
    } finally {
      setLoadingSuggest(false);
    }
  };

  const saveRevision = async () => {
    const amount = Number(newAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      setError(labels.editInvalid ?? "Montant invalide.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await createAdminBudgetRevision(row.convention_id, {
        period_key: row.period_key,
        budget_nature: budgetNature,
        revision_type: canSuggest ? "effectif" : "manual",
        new_amount: amount,
        effective_from: effectiveFrom || undefined,
        nb_familles_before: suggestion?.nb_familles_before ?? undefined,
        nb_familles_after: suggestion?.nb_familles_after ?? undefined,
        reason: reason.trim() || undefined,
      });
      onSaved(res.item);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="theme-card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{labels.revisionTitle}</h2>
            <p className="text-sm text-muted">
              {row.convention_name} · {row.period_key} ·{" "}
              <span className="uppercase">{row.type_traitement}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-border p-2">
            <X size={16} />
          </button>
        </div>

        <div className="mb-4 flex gap-2 border-b border-border">
          <button
            type="button"
            className={cn(
              "border-b-2 px-3 py-2 text-sm font-medium",
              tab === "revise" ? "border-primary text-primary" : "border-transparent text-muted",
            )}
            onClick={() => setTab("revise")}
          >
            {labels.revisionTab}
          </button>
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1 border-b-2 px-3 py-2 text-sm font-medium",
              tab === "history" ? "border-primary text-primary" : "border-transparent text-muted",
            )}
            onClick={() => setTab("history")}
          >
            <History size={14} />
            {labels.historyTab}
          </button>
        </div>

        {error && (
          <p className="mb-3 rounded-lg border border-pink/30 bg-pink-soft/30 px-3 py-2 text-sm text-pink">
            {error}
          </p>
        )}

        {tab === "revise" ? (
          <div className="space-y-4">
            <FormField label={labels.natureLabel}>
              <select
                className={selectClass}
                value={budgetNature}
                onChange={(e) => setBudgetNature(e.target.value)}
              >
                {natures.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </FormField>

            {canSuggest && (
              <>
                <FormField label={labels.effectiveFrom}>
                  <input
                    type="date"
                    className={inputClass}
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                </FormField>
                <button
                  type="button"
                  disabled={loadingSuggest}
                  onClick={() => void runSuggest()}
                  className="inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2 text-sm font-medium text-primary"
                >
                  <Sparkles size={15} />
                  {loadingSuggest ? labels.suggestLoading : labels.suggestEffectif}
                </button>
                {suggestion && (
                  <div className="rounded-lg border border-border bg-card-muted/40 p-3 text-sm">
                    <p>
                      {labels.effectifBefore}: <strong>{suggestion.nb_familles_before ?? "—"}</strong> →{" "}
                      {labels.effectifAfter}: <strong>{suggestion.nb_familles_after ?? "—"}</strong>
                      {suggestion.nb_familles_delta != null && suggestion.nb_familles_delta > 0
                        ? ` (+${suggestion.nb_familles_delta})`
                        : null}
                    </p>
                    {suggestion.detail && <p className="mt-1 text-muted">{suggestion.detail}</p>}
                    <p className="mt-2">
                      {labels.suggestedDelta}: {formatUsd(suggestion.suggested_delta)} ·{" "}
                      {labels.newBudget}: {formatUsd(suggestion.suggested_new_amount)}
                    </p>
                  </div>
                )}
              </>
            )}

            <FormField label={labels.newAmountLabel}>
              <input
                type="number"
                min="0"
                step="0.01"
                className={inputClass}
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
              />
            </FormField>

            <FormField label={labels.reasonLabel}>
              <textarea
                className={cn(inputClass, "min-h-[80px]")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={labels.reasonPlaceholder}
              />
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm">
                {labels.cancelEdit}
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void saveRevision()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {saving ? labels.saving : labels.saveRevision}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {loadingHistory ? (
              <p className="py-6 text-center text-sm text-muted">{labels.loading}</p>
            ) : history.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">{labels.historyEmpty}</p>
            ) : (
              <ul className="space-y-3">
                {history.map((rev) => (
                  <li key={rev.id} className="rounded-lg border border-border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-foreground">
                        {formatUsd(rev.previous_amount)} → {formatUsd(rev.new_amount)}
                        {rev.delta_amount !== 0 && (
                          <span className={rev.delta_amount > 0 ? " text-pink" : " text-green"}>
                            {" "}
                            ({rev.delta_amount > 0 ? "+" : ""}
                            {formatUsd(rev.delta_amount)})
                          </span>
                        )}
                      </span>
                      <span className="text-xs uppercase text-muted">{rev.revision_type}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted">
                      {rev.budget_nature} · {rev.effective_from ?? "—"} · {rev.created_at}
                    </p>
                    {rev.nb_familles_delta != null && rev.nb_familles_delta !== 0 && (
                      <p className="text-xs text-muted">
                        Effectif : {rev.nb_familles_before} → {rev.nb_familles_after} (
                        {rev.nb_familles_delta > 0 ? "+" : ""}
                        {rev.nb_familles_delta})
                      </p>
                    )}
                    {rev.reason && <p className="mt-1 text-foreground">{rev.reason}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
